import type { ThemeKey, DefaultThemeStyles } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { getKeyValue, t } from 'Src/i18n';
import { COLORS } from 'Components/utils/color/colors';
import { useAppContext } from 'Context/ReactAppContext';
import { getInstallmentPrice } from 'Components/utils/price/price.util';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { themes } from './PriceDiscount.themes';
type Props = {
  bestPrice?: FormattedPrice;
  color?: Colors;
  className?: string;
  from?: ReactNode;
  hasAsterisk?: boolean;
  hasDiscount?: boolean;
  hasInitialPrice?: boolean;
  hasInstalment?: boolean;
  hasTaxesAndFeesIncludedLabel?: boolean;
  hasTotalPrice?: boolean;
  initialPrice?: FormattedPrice;
  theme?: ThemeKey<typeof themes>;
  unit?: string;
};
export const PriceDiscount = ({
  bestPrice,
  className,
  color = COLORS.SIENNA,
  from,
  hasAsterisk,
  hasDiscount = false,
  hasInitialPrice = false,
  hasInstalment = true,
  hasTaxesAndFeesIncludedLabel,
  hasTotalPrice = false,
  initialPrice,
  theme = 'default',
  unit,
  ...rest
}: Props) => {
  const { locale } = useAppContext();
  const initialPriceFormattedValue =
    hasInstalment && initialPrice
      ? formatAmountForLocale(Math.floor(initialPrice.value), locale, initialPrice.currency, {
          precision: 0,
        })
      : initialPrice && initialPrice.formattedValue;
  const totalBestPrice = bestPrice && bestPrice.formattedValue;
  const bestPriceFormattedValue = hasInstalment
    ? getInstallmentPrice(bestPrice, locale)
    : totalBestPrice;
  const hasInitialPriceDisplayed =
    hasInitialPrice && initialPriceFormattedValue && (hasDiscount || !bestPrice);
  const hasTotalPriceDisplayed =
    hasTotalPrice && hasInstalment && bestPriceFormattedValue !== totalBestPrice;
  const { thBestPrice, thFirstLine, thFrom, thInitialPrice, thRoot, thTotalPrice, thUnit } = themes[
    theme
  ]({
    color,
    hasInitialPriceDisplayed,
  }) as DefaultThemeStyles<typeof themes>;
  return (
    <span {...rest} className={classnames(thRoot, className)}>
      {(hasInitialPriceDisplayed || hasTotalPriceDisplayed || from) && (
        <span className={thFirstLine}>
          {from && <span className={thFrom}>{from}</span>}
          {hasInitialPriceDisplayed && (
            <span className={thInitialPrice} data-testid="PriceItem-value">
              {initialPriceFormattedValue}
            </span>
          )}
          {hasTotalPriceDisplayed && (
            <span className={thTotalPrice} data-testid="PriceItem-value">
              {totalBestPrice}
            </span>
          )}
        </span>
      )}

      {bestPriceFormattedValue && (
        <span className={thBestPrice} data-testid="PriceItem-value">
          {bestPriceFormattedValue}
        </span>
      )}

      {unit && (
        <div className={thUnit}>
          {hasTaxesAndFeesIncludedLabel &&
          getKeyValue(
            t('booking.priceDetails.packagePrice.fees.displayTaxesAndFeesIncludedMention'),
          )
            ? null
            : unit}
          {hasAsterisk && <sup>*</sup>}
          {hasTaxesAndFeesIncludedLabel &&
            getKeyValue(
              t('booking.priceDetails.packagePrice.fees.displayTaxesAndFeesIncludedMention'),
            ) && <div>{t('booking.priceDetails.packagePrice.fees.taxesAndFeesIncludedLabel')}</div>}
        </div>
      )}
    </span>
  );
};
